import React, { useId, useMemo } from "react";

export interface CommunityRadarPoint {
  key: string;
  value: number;
}

interface CommunityRadarChartProps {
  points: ReadonlyArray<CommunityRadarPoint>;
  title: string;
  description: string;
  size?: number;
}

/**
 * Accessible radar chart for normalized 0–100 community alignment scores.
 * Labels are rendered in the detail grid beneath the chart, which keeps the
 * SVG locale-safe and avoids truncating translated text.
 */
const CommunityRadarChart: React.FC<CommunityRadarChartProps> = ({
  points,
  title,
  description,
  size = 300,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const center = size / 2;
  const radius = size * 0.32;

  const vertices = useMemo(
    () =>
      points.map((point, index) => {
        const angle = -Math.PI / 2 + (2 * Math.PI * index) / Math.max(points.length, 1);
        const normalizedValue = Math.max(0, Math.min(100, point.value));
        const pointRadius = (normalizedValue / 100) * radius;
        return {
          point,
          angle,
          x: center + Math.cos(angle) * pointRadius,
          y: center + Math.sin(angle) * pointRadius,
          axisX: center + Math.cos(angle) * radius,
          axisY: center + Math.sin(angle) * radius,
        };
      }),
    [center, points, radius]
  );

  if (points.length < 3) {
    return (
      <div
        role="img"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="flex min-h-[16rem] items-center justify-center rounded-2xl border border-dashed border-eoe-border/70 bg-eoe-bg-hover/40 px-4 py-8 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg-hover/30"
      >
        <div className="space-y-1 text-center">
          <p id={titleId} className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {title}
          </p>
          <p id={descriptionId} className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {description}
          </p>
        </div>
      </div>
    );
  }

  const polygonPoints = vertices.map((vertex) => `${vertex.x},${vertex.y}`).join(" ");

  return (
    <figure className="flex flex-col items-center gap-4">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="max-w-full overflow-visible"
      >
        <title id={titleId}>{title}</title>
        <desc id={descriptionId}>{description}</desc>
        {[0.25, 0.5, 0.75, 1].map((multiplier) => (
          <circle
            key={multiplier}
            cx={center}
            cy={center}
            r={radius * multiplier}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.15}
            strokeWidth={1}
            className="text-eoe-text-sub dark:text-eoe-dark-text-sub"
          />
        ))}
        {vertices.map((vertex) => (
          <line
            key={vertex.point.key}
            x1={center}
            y1={center}
            x2={vertex.axisX}
            y2={vertex.axisY}
            stroke="currentColor"
            strokeOpacity={0.2}
            strokeWidth={1}
            className="text-eoe-text-sub dark:text-eoe-dark-text-sub"
          />
        ))}
        <polygon
          points={polygonPoints}
          fill="rgba(37, 99, 235, 0.18)"
          stroke="rgb(37, 99, 235)"
          strokeWidth={2}
        />
        {vertices.map((vertex) => (
          <circle
            key={`${vertex.point.key}-dot`}
            cx={vertex.x}
            cy={vertex.y}
            r={4}
            fill="rgb(37, 99, 235)"
          />
        ))}
      </svg>
      <figcaption className="max-w-lg text-center text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {description}
      </figcaption>
    </figure>
  );
};

export default CommunityRadarChart;

